import React, { useEffect, useState } from "react";
import axios, { AxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import {
  setMultiTenancyConfigData,
  setAffiliateLoading,
} from "../redux/modules/multiTenancyConfig";
import config from "../clientConfig";
import { LoadingStripe } from "./loading";
import {
  pickConfigString,
  unwrapAffiliateConfig,
} from "../utils/tenancySeo";
import {
  applyMitoBrandToConfig,
  isMitoTenantHost,
  resolveAffiliateDomainHeader,
  resolveNavBarColour,
  resolvePrimaryColour,
  shouldUseMitoRed,
} from "../utils/mitoBrand";
import { applyMitoThemeClass, resolveMitoThemeName } from "../theme/mito";

type WidgetProps = {
  widgetConfig?: {
    frameKey?: string;
    [key: string]: any;
  };
  children?: React.ReactNode;
};

/** Stable axios instance */
const axiosInstance = axios.create({
  baseURL: config.apiUrl,
  headers: {
    "Content-Type": "application/json",
    Accept: "*/*",
  },
});

const StyleConfiguration: React.FC<WidgetProps> = ({
  widgetConfig,
  children,
}) => {
  const [affiliateConfig, setAffiliateConfig] = useState<any>(null);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { AffiliateLoading } = useAppSelector((state) => ({
    AffiliateLoading: state.affiliate.AffiliateLoading,
  }));

  /** Fetch Affiliate Configuration */
  const fetchAffiliateConfig = async (signal?: AbortSignal) => {
    try {
      const response = await axiosInstance.get(
        "/Affiliate/GetAffiliateConfiguration",
        {
          signal,
          headers: {
            apiKey: config.apiKey,
            "Frame-Authorization": localStorage.getItem("widgetToken") || "",
            affiliateId: localStorage.getItem("affiliateId") || "",
          },
        },
      );

      const data = response.data;
      const type = String(
        data?.responsetype || data?.responseType || "",
      ).toLowerCase();

      if (type === "error" || type === "fail") {
        dispatch(setAffiliateLoading(false));
        navigate("/notfound");
        return;
      }

      const payload = applyMitoBrandToConfig(unwrapAffiliateConfig(data));
      dispatch(setMultiTenancyConfigData(payload));
      setAffiliateConfig(payload);
      dispatch(setAffiliateLoading(false));

      const pageTitle = pickConfigString(
        payload,
        "siteSeoTitle",
        "SiteSeoTitle",
        "siteseotitle",
      );
      if (pageTitle && typeof document !== "undefined") {
        document.title = pageTitle;
      }
      const searchDescription = pickConfigString(
        payload,
        "siteSeoDescription",
        "SiteSeoDescription",
        "siteseodescription",
      );
      if (searchDescription && typeof document !== "undefined") {
        let meta = document.querySelector('meta[name="description"]');
        if (!meta) {
          meta = document.createElement("meta");
          meta.setAttribute("name", "description");
          document.head.appendChild(meta);
        }
        meta.setAttribute("content", searchDescription);
      }

      if (payload?.isActive === false || payload?.IsActive === false) {
        navigate("/domain-deactivated");
      }
    } catch (err) {
      if (axios.isCancel(err)) return;

      if (err instanceof AxiosError) {
      } else if (err instanceof Error) {
      } else {
      }

      dispatch(setAffiliateLoading(false));
      console.error("fetchAffiliateConfig:", err);
    }
  };

  const isMitoTenantSubdomain = () => isMitoTenantHost();

  /** Verify Super Affiliate */
  const verifySuperAffiliateUser = async (signal?: AbortSignal) => {
    dispatch(setAffiliateLoading(true));

    try {
      // API requires apiKey (FluentValidation). Subdomain tenants resolve by Origin host;
      // frame embeds still pass frameKey when not on a *.mito.money tenant host.
      const apiHeaders: Record<string, string> = {
        apiKey: config.apiKey || "",
      };

      // Local preview must not send localhost as the tenant host (API 404s).
      const affiliateDomain =
        resolveAffiliateDomainHeader() ||
        (widgetConfig?.domainName ? String(widgetConfig.domainName) : "");
      if (affiliateDomain) {
        apiHeaders["X-Affiliate-Domain"] = affiliateDomain;
      }

      if (!isMitoTenantSubdomain()) {
        const key = widgetConfig?.frameKey || config.frameKey;
        if (key) {
          apiHeaders.frameKey = key;
        }
      }

      const response = await axiosInstance.get(
        "/auth/VerifySuperAffiliateUser",
        {
          signal,
          headers: apiHeaders,
        },
      );

      const data = response.data;
      const type = String(
        data?.responsetype || data?.responseType || "",
      ).toLowerCase();
      const inner =
        data?.data && typeof data.data === "object" && !Array.isArray(data.data)
          ? data.data
          : {};
      const divert =
        inner.divertToMitoApex === true ||
        inner.DivertToMitoApex === true ||
        String(data?.message || "") === "SubdomainDisabled";

      if (type === "error" || type === "fail") {
        dispatch(setAffiliateLoading(false));
        if (divert || isMitoTenantSubdomain()) {
          const url =
            inner.redirectUrl ||
            inner.RedirectUrl ||
            "https://mito.money";
          window.location.replace(url);
          return;
        }
        navigate("/notfound");
        return;
      }

      localStorage.setItem("widgetToken", data?.data?.token || "");
      localStorage.setItem("affiliateId", data?.data?.affiliateId || "");

      await fetchAffiliateConfig(signal);
    } catch (err) {
      if (axios.isCancel(err)) return;

      if (err instanceof AxiosError) {
      } else if (err instanceof Error) {
      } else {
      }

      dispatch(setAffiliateLoading(false));
      console.error("verifySuperAffiliateUser:", err);
    }
  };

  /** Run ONCE */
  useEffect(() => {
    const controller = new AbortController();

    verifySuperAffiliateUser(controller.signal);

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const useMito = shouldUseMitoRed(affiliateConfig);
    applyMitoThemeClass(resolveMitoThemeName(useMito));
    return () => applyMitoThemeClass(null);
  }, [affiliateConfig]);

  /** Dynamic CSS — *.mito.money tenants always use MITO red. */
  const styleConfiguration = `
    :root {
      --dynamicPrimaryColor: ${resolvePrimaryColour(affiliateConfig)};
      --dynamicSecondaryColor: ${
        affiliateConfig?.secondaryColourCode ||
        resolvePrimaryColour(affiliateConfig)
      };
      --dynamicNavBarColor: ${resolveNavBarColour(affiliateConfig)};
    }
  `;

  /** Loader */
  if (AffiliateLoading) {
    return <LoadingStripe />;
  }

  return (
    <>
      <style>{styleConfiguration}</style>
      {children}
    </>
  );
};

export default StyleConfiguration;
